import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Flag, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useProject } from "@/pages/project/ProjectLayout";
import { GanttChart } from "@/components/planning/GanttChart";
import { ActivityDialog } from "@/components/planning/ActivityDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CHIP =
  "mono rounded-full px-2.5 py-1 text-[9.5px] uppercase tracking-wider transition-colors";

export default function PlanningPage() {
  const { projectId } = useProject();
  const [data, setData] = useState(null);
  const [presets, setPresets] = useState([]);
  const [options, setOptions] = useState({});
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ level: "", elevation: "", panel_type: "", type_code: "" });
  const [phaseFilter, setPhaseFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [dates, setDates] = useState({ start: "", finish: "" });
  const [form, setForm] = useState({ name: "", start: "", finish: "", preset_ids: [] });
  const [dialog, setDialog] = useState({ open: false, activity: null, phaseId: null });

  const load = useCallback(async () => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, value]) => value)
    ).toString();
    const [planning, presetList] = await Promise.all([
      api.get(`/projects/${projectId}/planning${query ? `?${query}` : ""}`),
      api.get(`/projects/${projectId}/presets`),
    ]);
    setData(planning.data);
    setPresets(presetList.data.presets || []);
    setDates({
      start: planning.data.project.start || "",
      finish: planning.data.project.finish || "",
    });
  }, [projectId, filters]);

  useEffect(() => {
    load().catch((error) => toast.error(apiError(error)));
  }, [load]);

  useEffect(() => {
    api
      .get(`/projects/${projectId}/panels/filter-options`)
      .then((res) => setOptions(res.data || {}))
      .catch(() => setOptions({}));
  }, [projectId]);

  const canPlan = !!data?.can_plan;

  const visiblePhases = useMemo(
    () => (data?.phases || []).filter((p) => !phaseFilter || p.id === phaseFilter),
    [data, phaseFilter]
  );
  const visibleActivities = useMemo(
    () =>
      (data?.activities || []).filter(
        (a) =>
          visiblePhases.some((p) => p.id === a.phase_id) && (!deptFilter || a.kind === deptFilter)
      ),
    [data, visiblePhases, deptFilter]
  );
  const phasesWithActivities = useMemo(
    () =>
      (data?.phases || []).map((p) => ({
        ...p,
        activities: (data?.activities || []).filter((a) => a.phase_id === p.id),
      })),
    [data]
  );

  const saveDates = async () => {
    try {
      await api.put(`/projects/${projectId}/planning/dates`, {
        start: dates.start || null,
        finish: dates.finish || null,
      });
      toast.success("Project dates saved");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const createPhase = async () => {
    if (!form.name.trim() || !form.start || !form.finish || form.preset_ids.length === 0) {
      toast.error("Phase name, start, finish and at least one preset are required");
      return;
    }
    setBusy(true);
    try {
      const { data: created } = await api.post(`/projects/${projectId}/planning/phases`, {
        name: form.name.trim(),
        start: form.start,
        finish: form.finish,
        preset_ids: form.preset_ids,
      });
      toast.success(`Phase created with ${created.panel_count} panels`);
      setForm({ name: "", start: "", finish: "", preset_ids: [] });
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const removePhase = async (phase) => {
    if (!window.confirm(`Delete phase "${phase.name}" and its activities?`)) return;
    try {
      await api.delete(`/projects/${projectId}/planning/phases/${phase.id}`);
      toast.success("Phase deleted");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const setBaseline = async (phase) => {
    try {
      await api.post(`/projects/${projectId}/planning/phases/${phase.id}/baseline`);
      toast.success("Baseline saved");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  if (!data)
    return (
      <main className="flex flex-1 items-center justify-center" data-testid="planning-loading">
        <Loader2 className="animate-spin text-cyan-600" />
      </main>
    );

  return (
    <main
      className="okn-scroll mx-auto w-full max-w-[1600px] flex-1 overflow-y-auto px-4 py-5 sm:px-6"
      data-testid="planning-page"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display flex items-center gap-2 text-xl font-medium tracking-tight sm:text-2xl">
          <CalendarRange size={18} className="text-cyan-600" /> Planning
          {!canPlan ? (
            <span
              data-testid="planning-readonly-badge"
              className="mono rounded-full bg-slate-100 px-2.5 py-1 text-[9.5px] uppercase tracking-wider text-slate-500"
            >
              Read only
            </span>
          ) : null}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="mono text-[9.5px] uppercase tracking-wider text-slate-500">
            Project window
          </span>
          <Input
            data-testid="project-start-input"
            type="date"
            value={dates.start}
            disabled={!canPlan}
            onChange={(e) => setDates((p) => ({ ...p, start: e.target.value }))}
            className="h-9 w-[150px] border-slate-200 bg-white text-xs"
          />
          <Input
            data-testid="project-finish-input"
            type="date"
            value={dates.finish}
            disabled={!canPlan}
            onChange={(e) => setDates((p) => ({ ...p, finish: e.target.value }))}
            className="h-9 w-[150px] border-slate-200 bg-white text-xs"
          />
          {canPlan ? (
            <Button
              data-testid="save-project-dates-button"
              onClick={saveDates}
              className="h-9 rounded-full bg-cyan-600 px-4 text-[10.5px] uppercase tracking-wider text-white hover:bg-cyan-500"
            >
              Save dates
            </Button>
          ) : null}
          <button
            data-testid="planning-refresh-button"
            onClick={() => load()}
            className="mono flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <select
          data-testid="gantt-phase-filter"
          value={phaseFilter}
          onChange={(e) => setPhaseFilter(e.target.value)}
          className="mono h-8 rounded-full border border-slate-200 bg-white px-3 text-[10px] uppercase tracking-wider text-slate-600"
        >
          <option value="">All phases</option>
          {(data.phases || []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          data-testid="gantt-department-filter"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="mono h-8 rounded-full border border-slate-200 bg-white px-3 text-[10px] uppercase tracking-wider text-slate-600"
        >
          <option value="">All departments</option>
          {(data.kinds || []).map((k) => (
            <option key={k.key} value={k.key}>
              {k.label}
            </option>
          ))}
        </select>
        {[
          ["level", "Level"],
          ["elevation", "Elevation"],
          ["panel_type", "Panel type"],
          ["type_code", "Type code"],
        ].map(([key, label]) => (
          <select
            key={key}
            data-testid={`gantt-${key}-filter`}
            value={filters[key]}
            onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
            className="mono h-8 rounded-full border border-slate-200 bg-white px-3 text-[10px] uppercase tracking-wider text-slate-600"
          >
            <option value="">All {label.toLowerCase()}</option>
            {(options[key] || []).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        ))}
      </div>

      {canPlan ? (
        <div
          data-testid="phase-creator"
          className="mb-4 rounded-xl border border-slate-200 bg-white p-4"
        >
          <p className="mono mb-3 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            New phase
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="phase-name-input"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Phase 01 — Level L02"
              className="h-9 w-full border-slate-200 bg-[#F8FAFC] text-xs sm:w-72"
            />
            <Input
              data-testid="phase-start-input"
              type="date"
              value={form.start}
              onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))}
              className="h-9 w-[150px] border-slate-200 bg-[#F8FAFC] text-xs"
            />
            <Input
              data-testid="phase-finish-input"
              type="date"
              value={form.finish}
              onChange={(e) => setForm((p) => ({ ...p, finish: e.target.value }))}
              className="h-9 w-[150px] border-slate-200 bg-[#F8FAFC] text-xs"
            />
            <Button
              data-testid="create-phase-button"
              onClick={createPhase}
              disabled={busy}
              className="h-9 rounded-full bg-cyan-600 px-4 text-[10.5px] uppercase tracking-wider text-white hover:bg-cyan-500"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Create phase"}
            </Button>
          </div>
          <p className="mono mt-3 text-[9.5px] uppercase tracking-wider text-slate-500">
            Select panels from filter presets
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {presets.length === 0 ? (
              <p className="text-[11.5px] text-slate-500">
                No saved presets yet — save filter presets from the Model tab first.
              </p>
            ) : null}
            {presets.map((preset) => {
              const active = form.preset_ids.includes(preset.id);
              const conditions = Object.entries(preset.filters || {}).filter(([, v]) => v);
              const manual = preset.panel_uids?.length || 0;
              const usable = conditions.length > 0 || manual > 0;
              return (
                <button
                  key={preset.id}
                  data-testid={`phase-preset-${preset.name}`}
                  disabled={!usable}
                  title={
                    manual
                      ? `${manual} manually selected panels`
                      : usable
                      ? conditions.map(([key, value]) => `${key}: ${value}`).join(" · ")
                      : "This preset has no filter conditions — re-save it from the Model tab"
                  }
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      preset_ids: active
                        ? p.preset_ids.filter((id) => id !== preset.id)
                        : [...p.preset_ids, preset.id],
                    }))
                  }
                  className={`${CHIP} ${
                    !usable
                      ? "border border-dashed border-slate-200 text-slate-400"
                      : active
                      ? "bg-cyan-500/18 text-cyan-700"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {preset.name}
                  {manual ? ` · ${manual} panels` : usable ? ` · ${conditions.length}` : " · no filter"}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <GanttChart
        phases={visiblePhases}
        activities={visibleActivities}
        projectStart={data.project.start}
        projectFinish={data.project.finish}
        canPlan={canPlan}
        onEditActivity={(activity) => setDialog({ open: true, activity, phaseId: activity.phase_id })}
      />

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {visiblePhases.map((phase) => (
          <div
            key={phase.id}
            data-testid={`phase-card-${phase.id}`}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-display truncate text-[15px] text-slate-900">{phase.name}</p>
                <p className="mono text-[9.5px] uppercase tracking-wider text-slate-500">
                  {phase.start} → {phase.finish} · {phase.panel_count} panels
                  {phase.late ? " · LATE" : ""}
                </p>
              </div>
              {canPlan ? (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    data-testid={`add-activity-${phase.id}`}
                    onClick={() => setDialog({ open: true, activity: null, phaseId: phase.id })}
                    title="Add activity"
                    className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-cyan-50 hover:text-cyan-700"
                  >
                    <Plus size={13} />
                  </button>
                  <button
                    data-testid={`baseline-${phase.id}`}
                    onClick={() => setBaseline(phase)}
                    title="Save baseline"
                    className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-slate-100"
                  >
                    <Flag size={13} />
                  </button>
                  <button
                    data-testid={`delete-phase-${phase.id}`}
                    onClick={() => removePhase(phase)}
                    title="Delete phase"
                    className="grid h-7 w-7 place-items-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ) : null}
            </div>
            <div className="mt-3 space-y-1.5">
              {(data.departments || []).map((dept) => {
                const stat = phase.progress?.[dept.key];
                if (!stat) return null;
                return (
                  <div key={dept.key} className="flex items-center gap-2">
                    <span className="mono w-[74px] shrink-0 text-[9.5px] uppercase tracking-wider text-slate-500">
                      {dept.label}
                    </span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full bg-cyan-500"
                        style={{ width: `${stat.percent}%` }}
                      />
                    </div>
                    <span
                      data-testid={`phase-${phase.id}-${dept.key}-percent`}
                      className="mono w-[62px] shrink-0 text-right text-[10px] text-slate-600"
                    >
                      {stat.percent}% ({stat.done}/{stat.total})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <ActivityDialog
        projectId={projectId}
        phases={phasesWithActivities}
        kinds={data.kinds || []}
        activity={dialog.activity}
        phaseId={dialog.phaseId}
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        onSaved={load}
      />
    </main>
  );
}
